import { useState, useEffect, useRef, useCallback } from "react";
import { Link, Redirect, useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
// import logo from "../../assets/img/logo.png";
import logo from "../../assets/img/lusuku_logo_main.png";
// import Navbar from "../../components/Navbar";
import useStyles from "./loginStyle.js";
import { Typography } from "@material-ui/core";
import { BounceLoader, MoonLoader } from "react-spinners";
import { GoogleLogin } from "react-google-login";
import { gapi } from "gapi-script";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import LinkIcon from "@material-ui/icons/Link";
import { loginFailure } from "../../redux/userRedux";
import { login, authenticateWithGoogle, fetchAuthOptions } from "../../redux/apiCalls";
import Layout from "../../components/Global/Layout.js";
import PhoneAuth from "../../components/PhoneAuth/PhoneAuth";

const Login = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID_AUTH_WEBAPP;
  const isMounted = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isGoogleApiLoaded, setGoogleApiLoaded] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [authMethod, setAuthMethod] = useState("phone"); // 'phone' or 'email'
  const [smsOtpEnabled, setSmsOtpEnabled] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const classes = useStyles();

  // Detect OAuth linking flow — user arrived from /oauth/authorize → ?next=<url>
  const location = useLocation();
  const history = useHistory();
  const nextUrl = new URLSearchParams(location.search).get("next") || "";
  const redirectUrl = new URLSearchParams(location.search).get("redirect") || "";
  const isOAuthLinking = nextUrl.includes("/oauth/authorize");

  // Handles sideEffects
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false; // Set the flag to false when unmounted
    };
  }, []);

  useEffect(() => {
    function start() {
      gapi.client.init({
        clientId: clientId,
        scope: "profile email",
      });
    }
    gapi.load("client: auth2", () => {
      start();
      setGoogleApiLoaded(true);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAuthOptions()
      .then((opts) => {
        if (cancelled || !opts) return;
        const enabled = opts.smsOtpEnabled !== false;
        setSmsOtpEnabled(enabled);
        // DELIBERATELY does NOT switch away from phone any more.
        //
        // It used to call setAuthMethod("email") here, which drops the customer
        // on the email + PASSWORD form. That is a dead end for the exact people
        // an SMS outage affects: phone-registered customers have never set a
        // password (the server returns PASSWORD_NOT_SET for them), so the
        // "fallback" could not be completed.
        //
        // The phone flow now handles the outage itself — the server reroutes
        // the code to the customer's email where it has one, and PhoneAuth asks
        // for an address where it doesn't. Switching away pre-emptively would
        // hide a path that works. `smsOtpEnabled` is still recorded so the UI
        // can show an advisory notice.

      })
      .catch(() => {
        if (!cancelled) setSmsOtpEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // const user = useSelector((state) => state.user.currentUser);
  const { currentUser, isFetching, error } = useSelector((state) => state.user); // get the currentUser from the store

  const [userData, setUserData] = useState({ email: "", password: "" });
  const dispatch = useDispatch();

  // Redirect back to previous page — prefer ?redirect= query param (set by Reviews / other
  // in-page prompts), then fall back to location.state.from (set by PrivateRoute).
  const { from } = location.state || { from: { pathname: redirectUrl || "/" } };

  // Keep a ref that always points to the latest currentUser so callbacks
  // (like PhoneAuth onSuccess) never capture a stale null.
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  // Shared redirect helper
  const doPostLoginRedirect = useCallback((user) => {
    if (isOAuthLinking && nextUrl) {
      const jwtToken = user?.token || user?.accessToken;
      try {
        const authorizeUrl = new URL(nextUrl);
        if (jwtToken) authorizeUrl.searchParams.set("token", jwtToken);
        window.location.href = authorizeUrl.toString();
      } catch {
        window.location.href = nextUrl;
      }
    } else {
      history.push(redirectUrl || from?.pathname || "/");
    }
  }, [isOAuthLinking, nextUrl, from, redirectUrl, history]);

  // OAuth redirect: when user becomes logged-in and we have a ?next= authorize URL,
  // navigate to the backend /oauth/authorize with the JWT appended.
  // This runs as an effect (proper place for side-effects) instead of during render.
  useEffect(() => {
    if (currentUser && isOAuthLinking && nextUrl) {
      doPostLoginRedirect(currentUser);
    }
  }, [currentUser, isOAuthLinking, nextUrl, doPostLoginRedirect]);

  // Non-OAuth: redirect already-logged-in users away from the login page.
  // Prefer ?redirect= query param (set by in-page prompts like Reviews),
  // then location.state.from (set by PrivateRoute), then "/".
  if (currentUser && !isOAuthLinking) {
    return <Redirect to={redirectUrl || from} />;
  }

  // OAuth: show a loading spinner while redirecting to the authorize endpoint
  if (currentUser && isOAuthLinking) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <BounceLoader size={35} color={"#078C03"} />
        <p style={{ marginTop: 16, fontSize: 14, color: '#7f8c8d', fontFamily: "'Inter', sans-serif" }}>
          Linking your account…
        </p>
      </div>
    );
  }

  const handleGoogleSuccess = async (response) => {
    setLoading(true);
    const { accessToken } = response;
    try {
      await authenticateWithGoogle(dispatch, {
        accessToken,
      });
      // Success - user will be redirected by the redirect logic
    } catch (error) {
      console.log(error, "Error from Google authentication...");
      toast.error("Failed to authenticate with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onFailure = (res) => {
    toast.error(res);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // dispatch(loginStart());
    try {
      await login(dispatch, userData);
    } catch (error) {
      // This will handle any errors that occur during the login process
      console.error(error);
      if (isMounted.current) {
        dispatch(
          loginFailure(error.message || "An error occurred during login.")
        );
      }
    }
  };

  const handleChange = ({ currentTarget: input }) => {
    setUserData({ ...userData, [input.name]: input.value });
  };

  return (
    <Layout>
      <div className={classes.login_nav_container}>
        {/* <Navbar /> */}
        <div className={classes.login_container}>
          <div className={classes.login_form_container}>
            <img src={logo} alt="logo" className={classes.logo_signin} />
            <div style={{ width: "100%" }}>
              <h1
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  margin: "0 0 5px 0",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontFamily: "'Poppins', sans-serif",
                  color: "#2c3e50",
                  letterSpacing: "-0.5px",
                }}
              >
                Welcome Back
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "400",
                  margin: "0 0 16px 0",
                  textAlign: "center",
                  fontFamily: "'Inter', sans-serif",
                  color: "#7f8c8d",
                }}
              >
                {isOAuthLinking
                  ? "Sign in to link your Lusuku account"
                  : "Sign in to continue to Lusuku"}
              </p>
              {isOAuthLinking && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    marginBottom: "24px",
                    textAlign: "left",
                  }}
                >
                  <LinkIcon style={{ fontSize: 20, color: "#15803d", flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <p
                      style={{
                        margin: "0 0 4px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#15803d",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      An AI assistant wants to connect to your account
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "#16a34a",
                        lineHeight: "1.5",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Sign in with your existing Lusuku account to continue.
                      New here?{" "}
                      <Link
                        to="/register"
                        style={{ color: "#15803d", fontWeight: "600" }}
                      >
                        Create an account
                      </Link>{" "}
                      — it only takes a minute.
                    </p>
                  </div>
                </div>
              )}
              {isFetching && (
                <div className={classes.login_loader}>
                  <BounceLoader size={35} color={"#078C03"} />
                </div>
              )}
              {!loading && authMethod === "phone" ? (
                <>
                  {!smsOtpEnabled && (
                    <Typography
                      style={{
                        textAlign: "center",
                        marginBottom: "14px",
                        fontSize: "13px",
                        lineHeight: 1.45,
                        fontFamily: "'Inter', sans-serif",
                        color: "#856404",
                        backgroundColor: "#fff8e6",
                        border: "1px solid #ffe082",
                        borderRadius: "8px",
                        padding: "9px 12px",
                      }}
                    >
                      {/* Two lines, not three. The old wording spent a whole
                          line saying "verification is temporarily" and another
                          on "below", neither of which the reader needs to act.
                          Same three facts, one line shorter. */}
                      SMS is down right now — we've picked WhatsApp for you. Email also works.
                    </Typography>
                  )}
                  {/* WhatsApp is the default channel for EVERYONE — richer
                      delivery, no per-message SMS cost — and it is also the
                      channel that keeps working when the server has SMS
                      switched off. SMS stays one tap away in the picker. */}
                  <PhoneAuth
                    onSuccess={() => doPostLoginRedirect(currentUserRef.current)}
                    initialChannel="whatsapp"
                    onUseEmailInstead={() => setAuthMethod("email")}
                  />
                  <Typography
                    style={{
                      textAlign: "center",
                      marginTop: "20px",
                      fontSize: "14px",
                      fontFamily: "'Inter', sans-serif",
                      color: "#7f8c8d",
                    }}
                  >
                    Or{" "}
                    <button
                      type="button"
                      onClick={() => setAuthMethod("email")}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#017109",
                        fontWeight: "600",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontFamily: "'Inter', sans-serif",
                        textDecoration: "underline",
                      }}
                    >
                      Use email instead
                    </button>
                  </Typography>
                </>
              ) : null}
              {!loading && authMethod === "email" && (
                <form onSubmit={handleSubmit}>
                  {/* Email and Password Inputs */}
                  <div className={classes.inputWrapper}>
                    <EmailIcon
                      className={classes.inputIcon}
                      style={{
                        color: focusedInput === "email" ? "#017109" : "#95a5a6",
                      }}
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter email address..."
                      onChange={handleChange}
                      onFocus={() => setFocusedInput("email")}
                      onBlur={() => setFocusedInput(null)}
                      className={classes.input}
                      value={userData.email}
                      required
                    />
                  </div>
                  <div className={classes.inputWrapper}>
                    <LockIcon
                      className={classes.inputIcon}
                      style={{
                        color:
                          focusedInput === "password" ? "#017109" : "#95a5a6",
                      }}
                    />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      onChange={handleChange}
                      onFocus={() => setFocusedInput("password")}
                      onBlur={() => setFocusedInput(null)}
                      className={classes.input}
                      style={{ paddingRight: "48px" }}
                      value={userData.password}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#95a5a6",
                        transition: "color 0.2s ease",
                        zIndex: 1,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#017109")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#95a5a6")}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon style={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityIcon style={{ fontSize: 20 }} />
                      )}
                    </button>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <Typography
                      variant="body2"
                      style={{
                        color: "#e74c3c",
                        fontSize: "13px",
                        backgroundColor: "#fee",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        marginBottom: "16px",
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      {error.message || error}
                    </Typography>
                  )}

                  {/* Forgot Password Link */}
                  <Typography
                    style={{
                      alignSelf: "flex-start",
                      marginBottom: "16px",
                      marginTop: "4px",
                    }}
                  >
                    <Link
                      href="#"
                      to="/forgot-password"
                      style={{
                        textDecoration: "none",
                        color: "#017109",
                        fontSize: "13px",
                        fontWeight: "600",
                        fontFamily: "'Inter', sans-serif",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) => (e.target.style.color = "#015a07")}
                      onMouseLeave={(e) => (e.target.style.color = "#017109")}
                    >
                      Forgot Password?
                    </Link>
                  </Typography>

                  {/* Login Button */}
                  <button
                    type="submit"
                    className={classes.login_button}
                    disabled={!userData.email || !userData.password}
                  >
                    Login
                  </button>

                  {/* Switch to Phone Auth. NOT hidden when SMS is off: the
                      phone flow survives an SMS outage (WhatsApp default,
                      email reroute), and a phone-registered customer on the
                      email+password form has no password — without this link
                      that form is a dead end on any market where SMS is
                      switched off (Zambia). */}
                  {(
                    <Typography
                      style={{
                        textAlign: "center",
                        marginTop: "20px",
                        marginBottom: "4px",
                        fontSize: "14px",
                        fontFamily: "'Inter', sans-serif",
                        color: "#7f8c8d",
                      }}
                    >
                      Or{" "}
                      <button
                        type="button"
                        onClick={() => setAuthMethod("phone")}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#017109",
                          fontWeight: "600",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontFamily: "'Inter', sans-serif",
                          textDecoration: "underline",
                          padding: 0,
                        }}
                      >
                        Use phone number instead
                      </button>
                    </Typography>
                  )}

                  {/* Divider */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "20px 0 20px 0",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "#e0e0e0",
                      }}
                    ></div>
                    <span
                      style={{
                        padding: "0 16px",
                        fontSize: "13px",
                        color: "#95a5a6",
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      OR
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        backgroundColor: "#e0e0e0",
                      }}
                    ></div>
                  </div>

                  {/* Google Authentication Button */}
                  <div id="googleSignin">
                    {isGoogleApiLoaded ? (
                      <GoogleLogin
                        clientId={clientId}
                        onSuccess={handleGoogleSuccess}
                        onFailure={onFailure}
                        buttonText="Continue with Google"
                        cookiePolicy="single_host_origin"
                        className={classes.google_sign_in_button}
                      />
                    ) : (
                      <div className={classes.google_sign_in_button}>
                        <div className={classes.googleSignInLoadingContainer}>
                          <MoonLoader color={"#FF5C16"} size={17} />
                        </div>
                        <span className={classes.loadingText}>
                          Loading Google auth...
                        </span>
                      </div>
                    )}
                  </div>

                  {/* New User Section */}
                  <Typography
                    style={{
                      textAlign: "center",
                      marginTop: "24px",
                      fontSize: "14px",
                      fontFamily: "'Inter', sans-serif",
                      color: "#7f8c8d",
                    }}
                  >
                    Don't have an account?{" "}
                    <Link
                      to={redirectUrl ? `/register?redirect=${encodeURIComponent(redirectUrl)}` : "/register"}
                      style={{
                        textDecoration: "none",
                        color: "#017109",
                        fontWeight: "600",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) => (e.target.style.color = "#015a07")}
                      onMouseLeave={(e) => (e.target.style.color = "#017109")}
                    >
                      Sign up
                    </Link>
                  </Typography>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
